import type { BatchSubmissionResultV2 } from './batchContract';
import type { CorrectionResultV2 } from './correctionContract';
import type { StudyTelemetryEventType } from '../studyV2/studyTelemetry';

export type StudyEvaluationMode =
  | 'study'
  | 'batch_submitting'
  | 'correction'
  | 'correction_submitting'
  | 'await_review'
  | 'completed'
  | 'error';

export type StudyEvaluationState = {
  mode: StudyEvaluationMode;
  clientBatchSubmissionId?: string;
  activeCorrectionSubmissionId?: string;
  correctionRound: number;
  remainingQuestionInstanceIds: string[];
  lastError?: string;
};

export type StudyEvaluationEffect = {
  telemetry: StudyTelemetryEventType[];
};

export type StudyEvaluationTransition = {
  state: StudyEvaluationState;
  effect: StudyEvaluationEffect;
};

export const INITIAL_STUDY_EVALUATION_STATE: StudyEvaluationState = {
  mode: 'study',
  correctionRound: 0,
  remainingQuestionInstanceIds: [],
};

function transition(state: StudyEvaluationState, patch: Partial<StudyEvaluationState>, telemetry: StudyTelemetryEventType[] = []): StudyEvaluationTransition {
  return { state: { ...state, ...patch }, effect: { telemetry } };
}

export function requestBatchSubmission(state: StudyEvaluationState, clientBatchSubmissionId: string): StudyEvaluationTransition {
  if (state.mode !== 'study' && state.mode !== 'error') throw new Error(`Cannot finish study from ${state.mode}.`);
  return transition(state, {
    mode: 'batch_submitting',
    clientBatchSubmissionId,
    activeCorrectionSubmissionId: undefined,
    correctionRound: 0,
    remainingQuestionInstanceIds: [],
    lastError: undefined,
  }, ['FINISH_REQUESTED']);
}

export function markBatchDispatched(state: StudyEvaluationState): StudyEvaluationTransition {
  if (state.mode !== 'batch_submitting') throw new Error(`Cannot dispatch batch from ${state.mode}.`);
  return transition(state, {}, ['SESSION_SUBMITTED']);
}

function effectiveBatchStatus(result: BatchSubmissionResultV2): Exclude<BatchSubmissionResultV2['status'], 'duplicate'> {
  if (result.status !== 'duplicate') return result.status;
  return result.originalStatus ?? 'rejected';
}

export function resolveBatchSubmission(state: StudyEvaluationState, result: BatchSubmissionResultV2): StudyEvaluationTransition {
  if (state.mode !== 'batch_submitting') throw new Error(`Cannot resolve batch from ${state.mode}.`);
  if (state.clientBatchSubmissionId && state.clientBatchSubmissionId !== result.clientBatchSubmissionId) {
    throw new Error('Batch result does not match active submission id.');
  }

  const status = effectiveBatchStatus(result);
  if (status === 'completed') {
    return transition(state, { mode: 'completed', remainingQuestionInstanceIds: [], lastError: undefined }, ['GRADE_RECEIVED', 'SESSION_COMPLETED']);
  }
  if (status === 'correction_required') {
    return transition(state, {
      mode: 'correction',
      remainingQuestionInstanceIds: [...result.correction.questionInstanceIds],
      lastError: undefined,
    }, ['GRADE_RECEIVED', 'CORRECTION_REQUIRED']);
  }
  if (status === 'review_required') {
    return transition(state, { mode: 'await_review', remainingQuestionInstanceIds: [], lastError: undefined }, ['GRADE_RECEIVED']);
  }
  return transition(state, { mode: 'error', lastError: 'Batch submission was rejected.' }, ['GRADE_RECEIVED']);
}

export function rejectBatchSubmission(state: StudyEvaluationState, error: unknown): StudyEvaluationTransition {
  if (state.mode !== 'batch_submitting') throw new Error(`Cannot reject batch from ${state.mode}.`);
  return transition(state, { mode: 'error', lastError: error instanceof Error ? error.message : String(error) });
}

export function requestCorrectionSubmission(state: StudyEvaluationState, clientCorrectionSubmissionId: string): StudyEvaluationTransition {
  if (state.mode !== 'correction') throw new Error(`Cannot submit correction from ${state.mode}.`);
  if (state.remainingQuestionInstanceIds.length === 0) throw new Error('Correction mode has no remaining targets.');
  return transition(state, {
    mode: 'correction_submitting',
    activeCorrectionSubmissionId: clientCorrectionSubmissionId,
    lastError: undefined,
  });
}

export function markCorrectionDispatched(state: StudyEvaluationState): StudyEvaluationTransition {
  if (state.mode !== 'correction_submitting') throw new Error(`Cannot dispatch correction from ${state.mode}.`);
  return transition(state, {}, ['CORRECTION_SUBMITTED']);
}

function effectiveCorrectionStatus(result: CorrectionResultV2): Exclude<CorrectionResultV2['status'], 'duplicate'> {
  if (result.status !== 'duplicate') return result.status;
  return result.originalStatus ?? 'correction_required';
}

export function resolveCorrectionSubmission(state: StudyEvaluationState, result: CorrectionResultV2): StudyEvaluationTransition {
  if (state.mode !== 'correction_submitting') throw new Error(`Cannot resolve correction from ${state.mode}.`);
  if (state.clientBatchSubmissionId && state.clientBatchSubmissionId !== result.clientBatchSubmissionId) {
    throw new Error('Correction result does not match active batch id.');
  }
  if (state.activeCorrectionSubmissionId && state.activeCorrectionSubmissionId !== result.clientCorrectionSubmissionId) {
    throw new Error('Correction result does not match active correction submission id.');
  }

  const status = effectiveCorrectionStatus(result);
  if (status === 'completed') {
    return transition(state, {
      mode: 'completed',
      correctionRound: Math.max(state.correctionRound, result.roundNumber),
      remainingQuestionInstanceIds: [],
      activeCorrectionSubmissionId: undefined,
      lastError: undefined,
    }, ['GRADE_RECEIVED', 'SESSION_COMPLETED']);
  }

  return transition(state, {
    mode: 'correction',
    correctionRound: Math.max(state.correctionRound, result.roundNumber),
    remainingQuestionInstanceIds: [...result.correction.remainingQuestionInstanceIds],
    activeCorrectionSubmissionId: undefined,
    lastError: undefined,
  }, ['GRADE_RECEIVED', 'CORRECTION_REQUIRED']);
}

export function rejectCorrectionSubmission(state: StudyEvaluationState, error: unknown): StudyEvaluationTransition {
  if (state.mode !== 'correction_submitting') throw new Error(`Cannot reject correction from ${state.mode}.`);
  return transition(state, {
    mode: 'correction',
    // Keep the client id so a network retry is idempotent even if the server
    // committed the previous request but the response was lost in transit.
    activeCorrectionSubmissionId: state.activeCorrectionSubmissionId,
    lastError: error instanceof Error ? error.message : String(error),
  });
}
